import React, { useState, Component } from 'react';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { IconButton } from '../components/SubscriptionAppIconButton';
import { Plus, Truck, Percent, Wine, GlassWater, Star, CreditCard, Ticket, X, Upload, ChevronDown, Tag as TagIcon, Info, AlertTriangle } from 'lucide-react';
import { Tag } from '../components/TagInput/Tag';
import { NavActions } from '../components/NavBar/NavActions';
import { Tabs } from '../components/Tabs';
import { Input } from '../components/SubscriptionAppInput';
import { useTags } from '../contexts/TagsContext';
// Approximate member counts per customer tag, used to sanity-check a
// tag-restricted reward before it goes live.
const tagMemberCounts: Record<string, number> = {
  premium: 412,
  vip: 86,
  loyal: 1240,
  'new-customer': 318,
  'needs-attention': 47,
  'high-value': 152,
  seasonal: 0,
  corporate: 24
};
// Catalogue stand-ins for the free product search
interface FreeProductOption {
  id: string;
  name: string;
  price: number;
  variants: string[];
}
const freeProductOptions: FreeProductOption[] = [{
  id: 'shiraz-2023',
  name: '2023 Hilltop Shiraz',
  price: 38,
  variants: ['750ml', '1.5L Magnum']
}, {
  id: 'cabernet-2022',
  name: '2022 Reserve Cabernet',
  price: 65,
  variants: ['750ml']
}, {
  id: 'chardonnay-2024',
  name: '2024 Estate Chardonnay',
  price: 32,
  variants: ['750ml', '375ml Half Bottle']
}, {
  id: 'pinot-museum',
  name: 'Museum Release Pinot Noir',
  price: 89,
  variants: ['750ml']
}, {
  id: 'blanc-de-blancs',
  name: 'Sparkling Blanc de Blancs',
  price: 45,
  variants: ['750ml', '1.5L Magnum', '375ml Half Bottle']
}];
// Catalogue stand-ins for the exclusive access search
const accessProductOptions = ['2023 Hilltop Shiraz', '2022 Reserve Cabernet', '2024 Estate Chardonnay', 'Museum Release Pinot Noir', 'Sparkling Blanc de Blancs'];
const accessCollectionOptions = ['Museum Releases', 'Library Reds', 'Single Vineyard', 'Cellar Door Exclusives', 'New Releases'];
// Icon config per reward type
const rewardIconConfig: Record<string, {
  icon: React.FC<{
    className?: string;
  }>;
  bg: string;
  color: string;
}> = {
  Shipping: {
    icon: Truck,
    bg: 'bg-[#DBEAFE]',
    color: 'text-[#3B82F6]'
  },
  Discount: {
    icon: Percent,
    bg: 'bg-[#D1FAE5]',
    color: 'text-[#10B981]'
  },
  Product: {
    icon: Wine,
    bg: 'bg-[#FEF3C7]',
    color: 'text-[#F59E0B]'
  },
  Experience: {
    icon: GlassWater,
    bg: 'bg-[#EDE9FE]',
    color: 'text-[#6F42C1]'
  },
  'Tier Up': {
    icon: Star,
    bg: 'bg-[#FEF3C7]',
    color: 'text-[#F59E0B]'
  },
  Credit: {
    icon: CreditCard,
    bg: 'bg-[#D1FAE5]',
    color: 'text-[#10B981]'
  },
  Voucher: {
    icon: Ticket,
    bg: 'bg-[#E0F2FE]',
    color: 'text-[#0EA5E9]'
  }
};
// Club discount ladder (reference data pulled from Club Builder)
const clubDiscountLadder = [{
  bottles: 3,
  tag: 'wineclub-3',
  discount: 8
}, {
  bottles: 6,
  tag: 'wineclub-6',
  discount: 10
}, {
  bottles: 12,
  tag: 'wineclub-12',
  discount: 13
}, {
  bottles: 18,
  tag: 'wineclub-18',
  discount: 15
}, {
  bottles: 24,
  tag: 'wineclub-24',
  discount: 18
}, {
  bottles: 36,
  tag: 'wineclub-36',
  discount: 22
}, {
  bottles: 48,
  tag: 'wineclub-48',
  discount: 25
}, {
  bottles: 72,
  tag: 'wineclub-72',
  discount: 30
}, {
  bottles: 144,
  tag: 'wineclub-144',
  discount: 35
}];
// Mock Data
const mockRewards = [{
  id: '1',
  name: 'Free Shipping',
  type: 'Shipping',
  points: 250,
  redeemed: 342,
  stock: '∞',
  minTier: 'Any',
  status: 'Active',
  restrictedTags: [] as string[]
}, {
  id: '2',
  name: '$10 Off Next Order',
  type: 'Discount',
  points: 1000,
  redeemed: 218,
  stock: '∞',
  minTier: 'Any',
  status: 'Active',
  restrictedTags: [] as string[]
}, {
  id: '3',
  name: 'Free Rosé Bottle',
  type: 'Product',
  points: 1500,
  redeemed: 89,
  stock: '23 remaining',
  minTier: 'Any',
  status: 'Active',
  restrictedTags: [] as string[]
}, {
  id: '4',
  name: 'Reserve Tasting for Two',
  type: 'Experience',
  points: 2500,
  redeemed: 56,
  stock: '50 remaining',
  minTier: 'Connoisseur',
  status: 'Active',
  restrictedTags: ['vip'] as string[]
}, {
  id: '5',
  name: 'Gold Status for 1 Month',
  type: 'Tier Up',
  points: 3000,
  redeemed: 12,
  stock: '∞',
  minTier: 'Any',
  status: 'Active',
  restrictedTags: [] as string[]
}, {
  id: '6',
  name: '$5 Account Credit',
  type: 'Credit',
  points: 500,
  redeemed: 0,
  stock: '∞',
  minTier: 'Any',
  status: 'Draft',
  restrictedTags: [] as string[]
}, {
  id: '7',
  name: 'Complimentary tasting experience',
  type: 'Voucher',
  worth: 'Experience',
  points: 0,
  redeemed: 12,
  stock: '∞',
  minTier: 'Any',
  status: 'Active',
  restrictedTags: [] as string[]
}];
export function LoyaltyRewardsPage() {
  const [activeTab, setActiveTab] = useState('All');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingReward, setEditingReward] = useState<(typeof mockRewards)[0] | null>(null);
  const [rewardType, setRewardType] = useState('Discount Code');
  const [creditAmount, setCreditAmount] = useState('10');
  const [upgradeTargetTier, setUpgradeTargetTier] = useState('');
  const [upgradeDuration, setUpgradeDuration] = useState('Matches your program');
  const [rewardStatus, setRewardStatus] = useState('Draft');
  const [productSearch, setProductSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedVariant, setSelectedVariant] = useState('');
  const selectedProduct = freeProductOptions.find(option => option.id === selectedProductId) ?? null;
  const productMatches = productSearch.trim() === '' ? [] : freeProductOptions.filter(option => option.name.toLowerCase().includes(productSearch.trim().toLowerCase()));
  const selectProduct = (product: FreeProductOption) => {
    setSelectedProductId(product.id);
    setSelectedVariant(product.variants[0]);
    setProductSearch('');
  };
  const [voucherKind, setVoucherKind] = useState<'money' | 'product' | 'experience'>('money');
  const [voucherAmount, setVoucherAmount] = useState('');
  const [voucherExperience, setVoucherExperience] = useState('');
  const [voucherRedeemInstructions, setVoucherRedeemInstructions] = useState('');
  const [voucherOnline, setVoucherOnline] = useState(true);
  const [voucherCellarDoor, setVoucherCellarDoor] = useState(true);
  const [voucherExpiryMonths, setVoucherExpiryMonths] = useState('6');
  const [voucherOnePerMember, setVoucherOnePerMember] = useState(true);
  const [applyMethod, setApplyMethod] = useState<'automatic' | 'code'>('automatic');
  const [accessScope, setAccessScope] = useState<'product' | 'collection'>('collection');
  const [accessSearch, setAccessSearch] = useState('');
  const [accessSelected, setAccessSelected] = useState<string[]>([]);
  const [accessDuration, setAccessDuration] = useState('90 days');
  const [accessEndDate, setAccessEndDate] = useState('');
  const accessSuggestions = accessScope === 'product' ? accessProductOptions : accessCollectionOptions;
  const accessMatches = accessSearch.trim() === '' ? [] : accessSuggestions.filter(option => option.toLowerCase().includes(accessSearch.trim().toLowerCase()) && !accessSelected.includes(option));
  const addAccessItem = (item: string) => {
    setAccessSelected(prev => prev.includes(item) ? prev : [...prev, item]);
    setAccessSearch('');
  };
  const [rewardName, setRewardName] = useState('10% Off Next Order');
  const [rewardDescription, setRewardDescription] = useState('Get 10% off your next wine order');
  const [rewardPoints, setRewardPoints] = useState('1000');
  const [discountType, setDiscountType] = useState<'flat' | 'percentage'>('percentage');
  const [toggles, setToggles] = useState({
    inventoryLimit: false,
    perCustomerLimit: false,
    cooldown: false,
    dateRange: false,
    minimumTier: true,
    minimumTenure: false,
    minimumSpend: false,
    clubStreak: false
  });
  const [minTier, setMinTier] = useState('Connoisseur');
  const [minSpendAmount, setMinSpendAmount] = useState('');
  const [minSpendPeriod, setMinSpendPeriod] = useState('Last 12 months');
  const [minSpendScope, setMinSpendScope] = useState<'all' | 'club' | 'oneTime'>('all');
  const [minTenureValue, setMinTenureValue] = useState('6');
  const [minTenureUnit, setMinTenureUnit] = useState<'Months' | 'Years'>('Months');
  const [clubStreakCount, setClubStreakCount] = useState('');
  const [showClubDiscounts, setShowClubDiscounts] = useState(false);
  const {
    allTags
  } = useTags();
  const [audienceMode, setAudienceMode] = useState<'all' | 'tags'>('all');
  const [restrictedTags, setRestrictedTags] = useState<string[]>([]);
  // A member qualifies on any one of the selected tags, so the reach is the sum
  // of each tag's members.
  const matchedMemberCount = restrictedTags.reduce((total, tagId) => total + (tagMemberCounts[tagId] ?? 0), 0);
  const toggleRestrictedTag = (tagId: string) => {
    setRestrictedTags(prev => prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]);
  };
  // Map table type to drawer select type
  const typeToSelectMap: Record<string, string> = {
    Shipping: 'Free Shipping',
    Discount: 'Discount Code',
    Product: 'Free Product',
    Experience: 'Experience',
    'Tier Up': 'Tier Upgrade',
    Credit: 'Account Credit',
    Voucher: 'Voucher'
  };
  const openCreateDrawer = () => {
    setEditingReward(null);
    setRewardType('Discount Code');
    setRewardName('');
    setRewardDescription('');
    setRewardPoints('');
    setDiscountType('percentage');
    setToggles({
      inventoryLimit: false,
      perCustomerLimit: false,
      cooldown: false,
      dateRange: false,
      minimumTier: false,
      minimumTenure: false,
      minimumSpend: false,
      clubStreak: false
    });
    setMinTier('Connoisseur');
    setAudienceMode('all');
    setRestrictedTags([]);
    setIsDrawerOpen(true);
  };
  const openEditDrawer = (reward: (typeof mockRewards)[0]) => {
    setEditingReward(reward);
    setRewardType(typeToSelectMap[reward.type] || 'Discount Code');
    setRewardName(reward.name);
    setRewardDescription(`${reward.name} reward`);
    setRewardPoints(String(reward.points));
    setDiscountType('percentage');
    const hasTier = reward.minTier !== 'Any';
    setToggles({
      inventoryLimit: false,
      perCustomerLimit: false,
      cooldown: false,
      dateRange: false,
      minimumTier: hasTier,
      minimumTenure: false,
      minimumSpend: false,
      clubStreak: false
    });
    setMinTier(hasTier ? reward.minTier : 'Connoisseur');
    setAudienceMode(reward.restrictedTags.length > 0 ? 'tags' : 'all');
    setRestrictedTags(reward.restrictedTags);
    if (reward.type === 'Voucher') {
      setVoucherKind('experience');
      setVoucherExperience('Complimentary tasting for two');
      setVoucherRedeemInstructions('Call or email us to book and quote your voucher code.');
      setVoucherOnline(true);
      setVoucherCellarDoor(true);
      setVoucherExpiryMonths('6');
      setVoucherOnePerMember(true);
    }
    setIsDrawerOpen(true);
  };
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingReward(null);
  };
  const tabs = [{
    id: 'All',
    label: 'All'
  }, {
    id: 'Active',
    label: 'Active'
  }, {
    id: 'Draft',
    label: 'Draft'
  }, {
    id: 'Archived',
    label: 'Archived'
  }];
  const filteredRewards = mockRewards.filter(reward => {
    if (activeTab === 'All') return true;
    return reward.status === activeTab;
  });
  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'Shipping':
        return 'info';
      case 'Discount':
        return 'success';
      case 'Product':
        return 'warning';
      case 'Experience':
        return 'default';
      // Purple isn't standard, using default or maybe info
      case 'Tier Up':
        return 'warning';
      case 'Credit':
        return 'success';
      case 'Voucher':
        return 'info';
      default:
        return 'default';
    }
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Draft':
        return 'default';
      // Grey
      case 'Archived':
        return 'default';
      // Muted/Grey
      default:
        return 'default';
    }
  };
  const getMinTierBadgeVariant = (tier: string) => {
    switch (tier) {
      case 'Any':
        return 'default';
      case 'Connoisseur':
        return 'info';
      default:
        return 'default';
    }
  };
  const handleToggle = (key: keyof typeof toggles) => {
    setToggles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6 relative" data-id="element-9898" data-mp="35emnikxzzz2f">
      {/* Header */}
      <div className="mb-6" data-id="element-9899" data-mp="30j7b7xfn2b84">
        <div className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-9900" data-mp="30ft7akrbttyk">
          <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-9901" data-mp="3o515cuibzxle">Rewards</h1>
          <div className="flex-1" data-id="element-9902" data-mp="3o2x3sj7wnaoa" />
          <Button variant="primary" size="md" onClick={openCreateDrawer} iconLeft={<Plus className="w-4 h-4" data-id="element-9904" data-mp="3a7mbndl1p3nm" />} data-id="element-9903" data-mp="3a5i7cuj8i2mt">
            Create Reward
          </Button>
          <NavActions data-id="element-9905" data-mp="3f2i7kotekb60" />
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6" data-id="element-9906" data-mp="30edtxz97ipu3">
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-9907" data-mp="30e5f41tixag9" />
      </div>

      {/* Data Table */}
      <div className="bg-white border border-ocean-border-subtle rounded-lg shadow-sm overflow-hidden" data-id="element-9908" data-mp="357zzz8kv7vgx">
        <div className="overflow-x-auto" data-id="element-9909" data-mp="2rcxn21hlp5um">
          <table className="w-full" data-id="element-9910" data-mp="2wewpn95inyqi">
            <thead className="bg-ocean-gray-50 border-b border-ocean-border-subtle" data-id="element-9911" data-mp="2wbr05ssz6uu9">
              <tr data-id="element-9912" data-mp="2hh48kckbn2a7">
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-9913" data-mp="2hix7f5op0i7n">
                  Reward
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-9914" data-mp="3pp9yw3fzks2z">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-9915" data-mp="3g08em12eroj7">
                  Points
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-9916" data-mp="3azuh3cyfnwzf">
                  Redeemed
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-9917" data-mp="31zfmto3hr7js">
                  Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-9918" data-mp="2s3mcmbd8vq56">
                  Min Tier
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-ocean-text-muted uppercase tracking-wider" data-id="element-9919" data-mp="qbg60zr6dixu">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ocean-border-subtle" data-id="element-9920" data-mp="12tselwszctmy">
              {filteredRewards.map(reward => <tr key={reward.id} onClick={() => openEditDrawer(reward)} className="hover:bg-ocean-gray-50 transition-colors cursor-pointer" data-id="element-9921" data-mp="17l21wqr23kvo">
                  <td className="px-6 py-4" data-id="element-9922" data-mp="1cjbwaz4yihg8">
                    <div className="flex items-center gap-3" data-id="element-9923" data-mp="ew4lkt6eny5k">
                      {(() => {
                    const config = rewardIconConfig[reward.type];
                    if (config) {
                      const IconComponent = config.icon;
                      return <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`} data-id="element-9924" data-mp="osprdgq91dag">
                              <IconComponent className={`w-4 h-4 ${config.color}`} data-id="element-9925" data-mp="tr10tqcmtls5" />
                            </div>;
                    }
                    return null;
                  })()}
                      <span className="text-sm font-medium text-ocean-text-primary" data-id="element-9926" data-mp="25adgjenib36m">
                        {reward.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9927" data-mp="9ki9jyi9cs5">
                    <Badge variant={getTypeBadgeVariant(reward.type)} data-id="element-9928" data-mp="e794ow880rzb">
                      {reward.type}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium text-ocean-text-primary" data-id="element-9929" data-mp="a3jigdf0qmhg">
                    {reward.points.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-ocean-text-secondary" data-id="element-9930" data-mp="3dnxng6ytee59">
                    {reward.redeemed.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-ocean-text-secondary" data-id="element-9931" data-mp="3mlq3jdcd1ptp">
                    {reward.stock}
                  </td>
                  <td className="px-6 py-4" data-id="element-9932" data-mp="2yuu35rtu9mau">
                    <div className="flex items-center gap-1.5" data-id="element-9933" data-mp="2uwejnxwyuw26">
                      <Badge variant={getMinTierBadgeVariant(reward.minTier)} data-id="element-9934" data-mp="2kroe7i2ct9t7">
                        {reward.minTier}
                      </Badge>
                      {reward.restrictedTags.length > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-[#E0F2FE] px-2 py-0.5 text-[11px] font-medium text-[#0369A1]" title={`Limited to members with ${reward.restrictedTags.length} tag${reward.restrictedTags.length === 1 ? '' : 's'}`} data-id="element-9935" data-mp="2pk7s6g4ye6yj">
                          <TagIcon className="w-3 h-3" data-id="element-9936" data-mp="33ipc02rqmzb6" />
                          Tag-gated
                        </span>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9937" data-mp="2tkroxilm78k5">
                    <Badge variant={getStatusBadgeVariant(reward.status)} data-id="element-9938" data-mp="2tjq8szpqrvvk">
                      {reward.status}
                    </Badge>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer Overlay */}
      {isDrawerOpen && <div className="fixed inset-0 bg-black/50 z-50 transition-opacity duration-300" onClick={closeDrawer} data-id="element-9939" data-mp="1n57j8jh3f8am" />}

      {/* Create/Edit Reward Drawer Panel */}
      <div className={`fixed top-0 right-0 h-full w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-9940" data-mp="zdr0wm74icsf">
        {/* Drawer Header */}
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between flex-shrink-0" data-id="element-9941" data-mp="qff4g3jksks2">
          <h2 className="text-lg font-semibold text-[#0F172A]" data-id="element-9942" data-mp="1n5z6h5ynh1h2">
            {editingReward ? 'Edit Reward' : 'Create Reward'}
          </h2>
          <IconButton icon={<X className="w-5 h-5" data-id="element-9944" data-mp="1dd08ixh9jyks" />} label="Close drawer" variant="ghost" onClick={closeDrawer} data-id="element-9943" data-mp="1ddjnq6fj48v4" />
        </div>

        {/* Drawer Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8" data-id="element-9945" data-mp="19adkl7npqxsv">
          {/* Section 1: Reward Basics */}
          <section data-id="element-9946" data-mp="213ntajca2ior">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-9947" data-mp="21349ex32hk83">
              Reward Basics
            </h3>
            <div className="space-y-4" data-id="element-9948" data-mp="3jrysdysyss67">
              <div data-id="element-9949" data-mp="3opgzdjnf8xbk">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9950" data-mp="3oop6igl7ldoz">
                  Type
                </label>
                <div className="relative" data-id="element-9951" data-mp="3spiv7c6h6wix">
                  <select value={rewardType} onChange={e => setRewardType(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-9952" data-mp="1krdjfif4mxl">
                    <option data-id="element-9953" data-mp="bgm5j18j7ud1">Discount Code</option>
                    <option data-id="element-9954" data-mp="gaqchkhss2e4">Free Shipping</option>
                    <option data-id="element-9955" data-mp="gc1h1mj2he6a">Free Product</option>
                    <option data-id="element-9956" data-mp="ka7iqg51t3kc">Experience</option>
                    <option data-id="element-9957" data-mp="kd3fi155tm77">Voucher</option>
                    <option data-id="element-9958" data-mp="pbeqif76y2r0">Account Credit</option>
                    <option data-id="element-9959" data-mp="p8irenjlx80z">Tier Upgrade</option>
                    <option data-id="element-9960" data-mp="uahxw6an8l25">Exclusive Access</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-9961" data-mp="jbya3zdudwb0" />
                </div>
              </div>

              <Input type="text" label="Name" value={rewardName} onChange={e => setRewardName(e.target.value)} placeholder="Enter reward name" data-id="element-9962" data-mp="neuxe3ebugla" />

              <div data-id="element-9963" data-mp="xc92j6usviya">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9964" data-mp="xcsmet40ghey">
                  Description
                </label>
                <textarea rows={2} value={rewardDescription} onChange={e => setRewardDescription(e.target.value)} placeholder="Describe this reward" className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none resize-none" data-id="element-9965" data-mp="16bw6o0ewz2v1" />
              </div>

              <Input type="number" label="Points Required" value={rewardPoints} onChange={e => setRewardPoints(e.target.value)} placeholder="0" data-id="element-9966" data-mp="1l1v4g22k4c9z" />

              <div data-id="element-9967" data-mp="1pxio61y9sd83">
                <label htmlFor="reward-status" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9968" data-mp="3ahns98v1dvdv">
                  Status
                </label>
                <div className="relative" data-id="element-9969" data-mp="35hjcxm5v76p8">
                  <select id="reward-status" value={rewardStatus} onChange={e => setRewardStatus(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-9970" data-mp="30j81x83u2q5f">
                    <option data-id="element-9971" data-mp="3o92dlmcf28o3">Draft</option>
                    <option data-id="element-9972" data-mp="3oa40qp6pilm6">Active</option>
                    <option data-id="element-9973" data-mp="3jic725s97zrt">Archived</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-9974" data-mp="3jg9a11sop6dl" />
                </div>
              </div>

              <div data-id="element-9975" data-mp="fv3kcsgtx1ea">
                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9976" data-mp="fvlqcgtamtze">
                  Image
                </label>
                <div className="h-20 border-2 border-dashed border-[#CBD5E1] rounded-lg bg-[#F8FAFC] flex flex-col items-center justify-center cursor-pointer hover:bg-[#F1F5F9] transition-colors" data-id="element-9977" data-mp="25awaw70p0ylv">
                  <Upload className="w-5 h-5 text-[#94A3B8] mb-1" data-id="element-9978" data-mp="1vl4dz654fte8" />
                  <span className="text-xs text-[#94A3B8]" data-id="element-9979" data-mp="1viqmt8dkb1by">Upload image</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Type-Specific Details */}
          {rewardType === 'Discount Code' && <section data-id="element-9980" data-mp="1qm9qvs04fiob">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-9981" data-mp="1qmjg3ddcdlsy">
                Discount Details
              </h3>
              <div className="space-y-4" data-id="element-9982" data-mp="1hl22q2rbjy7y">
                <div className="flex items-center gap-6" data-id="element-9983" data-mp="1hm4qw2x9bmk6">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-9984" data-mp="37003xo97xha3">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${discountType === 'flat' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDiscountType('flat')} data-id="element-9985" data-mp="36xx3eaafw5r4">
                      {discountType === 'flat' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-9986" data-mp="vd6cg3djezpk" />}
                    </div>
                    <span className="text-sm text-[#0F172A]" data-id="element-9987" data-mp="1a22gzqgdxulf">Flat amount</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-9988" data-mp="15akhl6u0g22k">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${discountType === 'percentage' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => setDiscountType('percentage')} data-id="element-9989" data-mp="1j1z1dfvqs7ao">
                      {discountType === 'percentage' && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-9990" data-mp="1szna59q6nfbu" />}
                    </div>
                    <span className="text-sm text-[#0F172A]" data-id="element-9991" data-mp="1o23jtbg7nc3j">Percentage</span>
                  </label>
                </div>

                <div data-id="element-9992" data-mp="3utmh5iw9vaiq">
                  <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9993" data-mp="3pt89i1yl22l8">
                    Value
                  </label>
                  <div className="relative" data-id="element-9994" data-mp="2gv8h2gsysged">
                    <input type="number" defaultValue="10" className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-9995" data-mp="2luux0y06gaqv" />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#64748B]" data-id="element-9996" data-mp="2uvsx557hvy46">
                      {discountType === 'percentage' ? '%' : '$'}
                    </div>
                  </div>
                </div>

                <div data-id="element-9997" data-mp="234ll2wel94lz">
                  <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-9998" data-mp="275yo6yqaqtp7">
                    Applies to
                  </label>
                  <div className="relative" data-id="element-9999" data-mp="2bxp0vbcphtjs">
                    <select className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10000" data-mp="2byi3c04mtww4">
                      <option data-id="element-10001" data-mp="1bwlep83t9ddw">Entire order</option>
                      <option data-id="element-10002" data-mp="1bw24yeobdvay">Specific products</option>
                      <option data-id="element-10003" data-mp="16x8smpkrkacd">Specific collections</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10004" data-mp="12vwyq7uux2yd" />
                  </div>
                </div>

                {/* Reference: Club discounts already set (read-only, from Club Builder) */}
                <div className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC]" data-id="element-10005" data-mp="t02amx7v6fo7">
                  <div className="flex items-center gap-2.5 px-3 py-2.5" data-id="element-10006" data-mp="o5y1q8f1fxkh">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#EDE9FE]" data-id="element-10007" data-mp="j97m1m75b3zl">
                      <Percent className="h-3.5 w-3.5 text-[#6F42C1]" data-id="element-10008" data-mp="f85mf49bgpx3" />
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-10009" data-mp="a6ye9439hopw">
                      <p className="text-xs font-medium text-[#475569]" data-id="element-10010" data-mp="57v6hbvwd4e8">
                        Club discounts already set
                      </p>
                      <p className="text-xs text-[#94A3B8]" data-id="element-10011" data-mp="3rovjerdq1qkm">
                        Runs 8% to 35% across 9 club tiers
                      </p>
                    </div>
                    <button type="button" onClick={() => setShowClubDiscounts(v => !v)} className="flex-shrink-0 text-xs font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-10012" data-mp="3ht2bxmev0n9p">
                      {showClubDiscounts ? 'Hide' : 'Show all'}
                    </button>
                  </div>
                  {showClubDiscounts && <div className="border-t border-[#E2E8F0] px-3 py-2" data-id="element-10013" data-mp="j9242fv4ml55">
                      <div className="divide-y divide-[#E2E8F0]" data-id="element-10014" data-mp="4m0hby897295">
                        {clubDiscountLadder.map(row => <div key={row.tag} className="flex items-center gap-3 py-1.5" data-id="element-10015" data-mp="9k25gxkjdrrh">
                            <span className="flex-shrink-0 text-xs text-[#0F172A]" data-id="element-10016" data-mp="xaefqf3980l8">
                              {row.bottles} bottles/yr
                            </span>
                            <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-[#64748B]" data-id="element-10017" data-mp="122pkbv8xwak4">
                              {row.tag}
                            </span>
                            <span className="flex-shrink-0 text-xs font-medium text-[#0F172A]" data-id="element-10018" data-mp="2hrx7za63kh2z">
                              {row.discount}% off
                            </span>
                          </div>)}
                      </div>
                      <p className="mt-2.5 text-[11px] leading-relaxed text-[#94A3B8]" data-id="element-10019" data-mp="23s5z1nuftqvu">
                        Best discount wins, so members already on a higher club
                        tier keep their club discount. This reward only changes
                        things for members whose club discount is below the
                        amount you set here.
                      </p>
                    </div>}
                </div>
              </div>
            </section>}

          {rewardType === 'Free Product' && <section data-id="element-10020" data-mp="3k7fj0k3awo6q">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10021" data-mp="3k5w1yr9tm93w">
                Product Details
              </h3>
              <div data-id="element-10022" data-mp="1mj3cwqa1z70y">
                <label htmlFor="reward-product-search" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10023" data-mp="1mik0tq733cuu">
                  Product
                </label>
                <input id="reward-product-search" type="text" value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Start typing to search products" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10024" data-mp="1rgbuaondn715" />

                {productMatches.length > 0 && <ul className="mt-1.5 border border-[#E2E8F0] rounded-md divide-y divide-[#E2E8F0] overflow-hidden" data-id="element-10025" data-mp="2a6thkdpfxaf4">
                    {productMatches.map(option => <li key={option.id} data-id="element-10026" data-mp="fvyd9nlaghi5">
                        <button type="button" onClick={() => selectProduct(option)} className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[#F8FAFC]" data-id="element-10027" data-mp="fwzq9xlv7e2i">
                          <span className="text-sm text-[#0F172A]" data-id="element-10028" data-mp="2y640kv5cyry9">
                            {option.name}
                          </span>
                          <span className="text-sm text-[#64748B] flex-shrink-0" data-id="element-10029" data-mp="2pb663jmkb74t">
                            ${option.price.toFixed(2)}
                          </span>
                        </button>
                      </li>)}
                  </ul>}

                {selectedProduct && <div className="mt-2.5 flex items-center justify-between gap-3 border border-[#E2E8F0] rounded-md px-3 py-2.5" data-id="element-10030" data-mp="3h0izkkro6km3">
                    <div className="min-w-0" data-id="element-10031" data-mp="381nrt48z92go">
                      <p className="text-sm font-medium text-[#0F172A] m-0 break-words" data-id="element-10032" data-mp="380cgmjd7csfj">
                        {selectedProduct.name}
                      </p>
                      <p className="text-xs text-[#64748B] mt-0.5 m-0" data-id="element-10033" data-mp="1wna7g9dfn55e">
                        ${selectedProduct.price.toFixed(2)}
                      </p>
                    </div>
                    <button type="button" onClick={() => {
                setSelectedProductId(null);
                setSelectedVariant('');
              }} aria-label={`Remove ${selectedProduct.name}`} className="text-[#94A3B8] hover:text-[#0F172A] flex-shrink-0" data-id="element-10034" data-mp="pvrkbjyab2i3">
                      <X className="w-4 h-4" data-id="element-10035" data-mp="yqfmv2ch6637" />
                    </button>
                  </div>}

                {selectedProduct && selectedProduct.variants.length > 1 && <div className="mt-3" data-id="element-10036" data-mp="18lhcdzanxa6b">
                    <label htmlFor="reward-product-variant" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10037" data-mp="13no9pg8kiyas">
                      Variant
                    </label>
                    <div className="relative" data-id="element-10038" data-mp="1hlcuyp6js7gd">
                      <select id="reward-product-variant" value={selectedVariant} onChange={e => setSelectedVariant(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10039" data-mp="1dlc2i0mm5vqb">
                        {selectedProduct.variants.map(variant => <option key={variant} data-id="element-10040" data-mp="35bo1kkpx55d4">{variant}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10041" data-mp="39cpzmy83nbdi" />
                    </div>
                  </div>}

                <p className="text-xs text-[#64748B] mt-1.5" data-id="element-10042" data-mp="30gqpt6198hwj">
                  The product added to the member's order when they claim this
                  reward.
                </p>
              </div>
            </section>}

          {rewardType === 'Voucher' && <section data-id="element-10043" data-mp="2clee3jcpcnel">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10044" data-mp="2cm79xpa4hshy">
                Voucher Details
              </h3>
              <div className="space-y-4" data-id="element-10045" data-mp="atj375y9smxf">
                <div data-id="element-10046" data-mp="aukpy7onexuz">
                  <span className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10047" data-mp="au1bj0y7irlp">
                    Voucher is for
                  </span>
                  <div className="space-y-2" data-id="element-10048" data-mp="de7d51zcm5z3">
                    {[{
                  id: 'money' as const,
                  label: 'Money off'
                }, {
                  id: 'product' as const,
                  label: 'A product'
                }, {
                  id: 'experience' as const,
                  label: 'An experience'
                }].map(option => <label key={option.id} className="flex items-center gap-2 cursor-pointer" data-id="element-10049" data-mp="3vlndg1k32oui">
                        <input type="radio" name="voucherKind" checked={voucherKind === option.id} onChange={() => setVoucherKind(option.id)} className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10050" data-mp="x3hpc5zvy8sg" />
                        <span className="text-sm text-[#0F172A]" data-id="element-10051" data-mp="n9si98zprmd2">
                          {option.label}
                        </span>
                      </label>)}
                  </div>

                  {voucherKind === 'money' && <div className="mt-3 animate-ocean-slide-up" data-id="element-10052" data-mp="1b1g4vepy0b9j">
                      <label htmlFor="voucher-amount" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10053" data-mp="1b051hg0et5iy">
                        Amount
                      </label>
                      <div className="relative" data-id="element-10054" data-mp="1ncgt4l88sr8w">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748B] pointer-events-none" data-id="element-10055" data-mp="1sas3cx2g93rn">
                          $
                        </span>
                        <input id="voucher-amount" type="number" value={voucherAmount} onChange={e => setVoucherAmount(e.target.value)} placeholder="50" className="w-full h-10 pl-7 pr-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10056" data-mp="1x7iini6fjvc0" />
                      </div>
                    </div>}

                  {voucherKind === 'product' && <div className="mt-3 animate-ocean-slide-up" data-id="element-10057" data-mp="2kzzd6s7ctfs7">
                      <label htmlFor="voucher-product-search" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10058" data-mp="2kxmlhnbsnr1y">
                        Product
                      </label>
                      <input id="voucher-product-search" type="text" value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Start typing to search products" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10059" data-mp="3mo32iszpfy0d" />

                      {productMatches.length > 0 && <ul className="mt-1.5 border border-[#E2E8F0] rounded-md divide-y divide-[#E2E8F0] overflow-hidden" data-id="element-10060" data-mp="33t5wdbp706pu">
                          {productMatches.map(option => <li key={option.id} data-id="element-10061" data-mp="3cssvtryd32g9">
                              <button type="button" onClick={() => selectProduct(option)} className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[#F8FAFC]" data-id="element-10062" data-mp="3cpdhwsgfbzgf">
                                <span className="text-sm text-[#0F172A]" data-id="element-10063" data-mp="s3lo56luzfa9">
                                  {option.name}
                                </span>
                                <span className="text-sm text-[#64748B] flex-shrink-0" data-id="element-10064" data-mp="viditf2v3qve">
                                  ${option.price.toFixed(2)}
                                </span>
                              </button>
                            </li>)}
                        </ul>}

                      {selectedProduct && <div className="mt-2.5 flex items-center justify-between gap-3 border border-[#E2E8F0] rounded-md px-3 py-2.5" data-id="element-10065" data-mp="ql4xj5mf36px">
                          <div className="min-w-0" data-id="element-10066" data-mp="1ecab1e4vxmx2">
                            <p className="text-sm font-medium text-[#0F172A] m-0 break-words" data-id="element-10067" data-mp="1j7ze3rg45ly4">
                              {selectedProduct.name}
                            </p>
                            <p className="text-xs text-[#64748B] mt-0.5 m-0" data-id="element-10068" data-mp="19ioaulfwrbi5">
                              ${selectedProduct.price.toFixed(2)}
                            </p>
                          </div>
                          <button type="button" onClick={() => {
                    setSelectedProductId(null);
                    setSelectedVariant('');
                  }} aria-label={`Remove ${selectedProduct.name}`} className="text-[#94A3B8] hover:text-[#0F172A] flex-shrink-0" data-id="element-10069" data-mp="21zs4rf7pmv2o">
                            <X className="w-4 h-4" data-id="element-10070" data-mp="21wjnvna9xmuf" />
                          </button>
                        </div>}

                      {selectedProduct && selectedProduct.variants.length > 1 && <div className="mt-3" data-id="element-10071" data-mp="1swmtd517dtrg">
                          <label htmlFor="voucher-product-variant" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10072" data-mp="1sxeoyfusvrhq">
                            Variant
                          </label>
                          <div className="relative" data-id="element-10073" data-mp="1j7lghnpyfshp">
                            <select id="voucher-product-variant" value={selectedVariant} onChange={e => setSelectedVariant(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10074" data-mp="1e80f76nbb81t">
                              {selectedProduct.variants.map(variant => <option key={variant} data-id="element-10075" data-mp="3gwi6bxsbrdgp">{variant}</option>)}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10076" data-mp="3lvbmjxyzxikk" />
                          </div>
                        </div>}
                    </div>}

                  {voucherKind === 'experience' && <div className="mt-3 animate-ocean-slide-up" data-id="element-10077" data-mp="2y57gzl28z8qx">
                      <label htmlFor="voucher-experience" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10078" data-mp="324ztu9yoc123">
                        Experience name
                      </label>
                      <input id="voucher-experience" type="text" value={voucherExperience} onChange={e => setVoucherExperience(e.target.value)} placeholder="Complimentary tasting for two" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10079" data-mp="3bv17s8xbc33a" />
                    </div>}
                </div>

                <div data-id="element-10080" data-mp="17te5784w0gls">
                  <label htmlFor="voucher-redeem" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10081" data-mp="17u5slvobz80z">
                    How to redeem
                  </label>
                  <textarea id="voucher-redeem" rows={3} value={voucherRedeemInstructions} onChange={e => setVoucherRedeemInstructions(e.target.value)} placeholder="Call or email us to book and quote your voucher code." className="w-full px-3 py-2 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none resize-none" data-id="element-10082" data-mp="1hqivnk5rmy8e" />
                  <p className="text-xs text-[#64748B] mt-1.5" data-id="element-10083" data-mp="20hs7w16x813o">
                    Shown on the voucher email and in the member portal.
                  </p>
                </div>

                <div data-id="element-10084" data-mp="29izu3kw5xbhe">
                  <span className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10085" data-mp="29jjd74xju5x0">
                    Where it can be used
                  </span>
                  <div className="space-y-2" data-id="element-10086" data-mp="2y2l581bm0qwk">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10087" data-mp="2y2bcw733e7np">
                      <input type="checkbox" checked={voucherOnline} onChange={() => setVoucherOnline(v => !v)} className="rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10088" data-mp="2k20l91tykp1h" />
                      <span className="text-sm text-[#0F172A]" data-id="element-10089" data-mp="2acz252s49rja">Online</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-10090" data-mp="2f48vadcvjczx">
                      <input type="checkbox" checked={voucherCellarDoor} onChange={() => setVoucherCellarDoor(v => !v)} className="rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10091" data-mp="20fl0486xx5ai" />
                      <span className="text-sm text-[#0F172A]" data-id="element-10092" data-mp="1remw3pwi4xrx">Cellar door</span>
                    </label>
                  </div>
                </div>

                <div data-id="element-10093" data-mp="1wcoc21ymsgz6">
                  <label htmlFor="voucher-expiry" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10094" data-mp="1wfah9uy1uke8">
                    Expires after
                  </label>
                  <div className="relative" data-id="element-10095" data-mp="2rfs47cibvfq5">
                    <input id="voucher-expiry" type="number" min="1" value={voucherExpiryMonths} onChange={e => setVoucherExpiryMonths(e.target.value)} className="w-full h-10 pl-3 pr-20 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10096" data-mp="2dp51l99g55os" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#94A3B8] pointer-events-none" data-id="element-10097" data-mp="28q26ohn6kfuj">
                      months
                    </span>
                  </div>
                  <p className="text-xs text-[#64748B] mt-1.5" data-id="element-10098" data-mp="1zq5dq3trcuto">
                    Counted from the day the voucher is issued.
                  </p>
                </div>

                <div data-id="element-10099" data-mp="3u2v6ujcay000">
                  <label className="flex items-start gap-2 cursor-pointer" data-id="element-10100" data-mp="3u3eodyy1imdi">
                    <input type="checkbox" checked={voucherOnePerMember} onChange={() => setVoucherOnePerMember(v => !v)} className="mt-0.5 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10101" data-mp="81fqzicmwu5k" />
                    <span className="text-sm text-[#0F172A]" data-id="element-10102" data-mp="guj2hl3pywxk">
                      Only one active voucher of this kind per member
                    </span>
                  </label>
                  <p className="text-xs text-[#64748B] mt-1.5" data-id="element-10103" data-mp="qs6xmc58m6g9">
                    A member won't get another until this one is used or
                    expires.
                  </p>
                </div>

                <div className="rounded-md border border-[#BAE6FD] bg-[#F0F9FF] px-3 py-2.5 flex items-start gap-2" data-id="element-10104" data-mp="zsll9i5obxmx">
                  <Info className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-10105" data-mp="vr0e8dftmdkn" />
                  <p className="text-xs text-[#0369A1] m-0" data-id="element-10106" data-mp="31jysidv46hm5">
                    Issuing this voucher sends a Loyalty Voucher Issued event to
                    Klaviyo, so you can send it as a voucher-style email.
                  </p>
                </div>
              </div>
            </section>}

          {rewardType === 'Account Credit' && <section data-id="element-10107" data-mp="15hvcj3sxmpud">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10108" data-mp="15hc185xsezpb">
                Credit Details
              </h3>
              <div className="space-y-4" data-id="element-10109" data-mp="1fft0rraskmmz">
                <div data-id="element-10110" data-mp="1fgktmud0869k">
                  <label htmlFor="reward-credit-amount" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10111" data-mp="1fcx1nzn7jrwy">
                    Credit Amount
                  </label>
                  <div className="relative" data-id="element-10112" data-mp="1oe4ysedzmmpg">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748B] pointer-events-none" data-id="element-10113" data-mp="1t64rpmvn4fcj">
                      $
                    </span>
                    <input id="reward-credit-amount" type="number" value={creditAmount} onChange={e => setCreditAmount(e.target.value)} className="w-full h-10 pl-7 pr-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10114" data-mp="1y7lq9bqnjppy" />
                  </div>
                  <p className="text-xs text-[#64748B] mt-1.5" data-id="element-10115" data-mp="3nmd8gtkvxeji">
                    The credit applied to the member's next order.
                  </p>
                </div>

                <div className="rounded-md border border-[#BAE6FD] bg-[#F0F9FF] px-3 py-2.5 flex items-start gap-2" data-id="element-10116" data-mp="3dop216dx2ili">
                  <Info className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-10117" data-mp="f7l3slfe8tl8" />
                  <p className="text-xs text-[#0369A1] m-0" data-id="element-10118" data-mp="a803a6kksd6e">
                    Account credit is applied automatically at checkout. Any
                    unused balance stays on the member's account for next time.
                  </p>
                </div>
              </div>
            </section>}

          {rewardType === 'Tier Upgrade' && <section data-id="element-10119" data-mp="xymda3e547j1">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10120" data-mp="xz5icjioz1fn">
                Upgrade Details
              </h3>
              <div className="space-y-4" data-id="element-10121" data-mp="dp2y7bar9sxf">
                <div data-id="element-10122" data-mp="do1kt069oucj">
                  <label htmlFor="reward-target-tier" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10123" data-mp="dqxlgw9i1x4o">
                    Target Tier
                  </label>
                  <div className="relative" data-id="element-10124" data-mp="3ryl00yw17sr">
                    <select id="reward-target-tier" value={upgradeTargetTier} onChange={e => setUpgradeTargetTier(e.target.value)} className={`w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white ${upgradeTargetTier === '' ? 'text-[#94A3B8]' : 'text-[#0F172A]'}`} data-id="element-10125" data-mp="8pqegzf6l1z2">
                      <option value="" data-id="element-10126" data-mp="mnf0iakzif5p">Select a tier</option>
                      <option value="Enthusiast" data-id="element-10127" data-mp="rnkkd4l0ixd8">Enthusiast</option>
                      <option value="Connoisseur" data-id="element-10128" data-mp="1feghmh88mkrv">Connoisseur</option>
                      <option value="Sommelier" data-id="element-10129" data-mp="1fc3tfmbvze6d">Sommelier</option>
                      <option value="Vintner" data-id="element-10130" data-mp="1k5otrl0yck8q">Vintner</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10131" data-mp="34uaf19qfknli" />
                  </div>
                </div>

                <div data-id="element-10132" data-mp="3do5kkivgsg1o">
                  <label htmlFor="reward-upgrade-duration" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10133" data-mp="3dmkr6wkvz35e">
                    Duration
                  </label>
                  <div className="relative" data-id="element-10134" data-mp="2lvy3xifyl3na">
                    <select id="reward-upgrade-duration" value={upgradeDuration} onChange={e => setUpgradeDuration(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10135" data-mp="2lwzpigulp8j9">
                      <option data-id="element-10136" data-mp="2q0e8ngdu650x">Matches your program</option>
                      <option data-id="element-10137" data-mp="a5lzq1f48cgw">3 months</option>
                      <option data-id="element-10138" data-mp="57u9rcy170fc">6 months</option>
                      <option data-id="element-10139" data-mp="570zw33rzusz">12 months</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10140" data-mp="2z0bvysyixx5d" />
                  </div>
                  <p className="text-xs text-[#64748B] mt-1.5" data-id="element-10141" data-mp="340zs1t1m146g">
                    Your program keeps tiers once earned, so this upgrade is
                    permanent. Choose a fixed duration to run a limited-time
                    promotion instead.
                  </p>
                </div>

                <div className="rounded-md border border-[#BAE6FD] bg-[#F0F9FF] px-3 py-2.5 flex items-start gap-2" data-id="element-10142" data-mp="2g77yewi6r05h">
                  <Info className="w-4 h-4 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-10143" data-mp="2p9zbf0qpm3z8" />
                  <p className="text-xs text-[#0369A1] m-0" data-id="element-10144" data-mp="2p7wdzvn898kh">
                    Members already at or above the target tier will not see
                    this reward.
                  </p>
                </div>
              </div>
            </section>}

          {rewardType === 'Exclusive Access' && <section data-id="element-10145" data-mp="21ibr51nydr5x">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10146" data-mp="1u0vmujaew4mr">
                Access Details
              </h3>
              <div className="space-y-4" data-id="element-10147" data-mp="1p33whtpf0qko">
                <div className="inline-flex rounded-md border border-[#CBD5E1] bg-white p-0.5" role="group" aria-label="Access applies to" data-id="element-10148" data-mp="1ozziy5q28ihc">
                  {[{
                id: 'product' as const,
                label: 'Product'
              }, {
                id: 'collection' as const,
                label: 'Collection'
              }].map(option => <button key={option.id} type="button" onClick={() => {
                setAccessScope(option.id);
                setAccessSearch('');
                setAccessSelected([]);
              }} aria-pressed={accessScope === option.id} className={`px-3 h-8 rounded text-sm font-medium transition-colors ${accessScope === option.id ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-10149" data-mp="16a9qhp9rs8sl">
                      {option.label}
                    </button>)}
                </div>

                <div data-id="element-10150" data-mp="2b9o9btn8jmsf">
                  <label htmlFor="reward-access-search" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10151" data-mp="2b720zrsit395">
                    {accessScope === 'product' ? 'Search products' : 'Search collections'}
                  </label>
                  <input id="reward-access-search" type="text" value={accessSearch} onChange={e => setAccessSearch(e.target.value)} placeholder="Start typing to search" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10152" data-mp="269ip2yjap2kd" />

                  {accessMatches.length > 0 && <ul className="mt-1.5 border border-[#E2E8F0] rounded-md divide-y divide-[#E2E8F0] overflow-hidden" data-id="element-10153" data-mp="1njapuripuw9g">
                      {accessMatches.map(option => <li key={option} data-id="element-10154" data-mp="19kb0fhndqdc8">
                          <button type="button" onClick={() => addAccessItem(option)} className="w-full text-left px-3 py-2 text-sm text-[#0F172A] hover:bg-[#F8FAFC]" data-id="element-10155" data-mp="1eiw09fypyv05">
                            {option}
                          </button>
                        </li>)}
                    </ul>}

                  {accessSelected.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2.5" data-id="element-10156" data-mp="13y0s2vsrw9h9">
                      {accessSelected.map(item => <span key={item} className="inline-flex items-center gap-1.5 rounded-full bg-[#E0F2FE] px-2.5 py-1 text-xs font-medium text-[#0369A1]" data-id="element-10157" data-mp="zvnk00qjzzhb">
                          {item}
                          <button type="button" onClick={() => setAccessSelected(prev => prev.filter(entry => entry !== item))} aria-label={`Remove ${item}`} className="text-[#0369A1] hover:text-[#075985]" data-id="element-10158" data-mp="18xdexgjn9gju">
                            <X className="w-3 h-3" data-id="element-10159" data-mp="1imeqmuj5pbs0" />
                          </button>
                        </span>)}
                    </div>}
                </div>

                <div data-id="element-10160" data-mp="1wk3b419bagwj">
                  <label htmlFor="reward-access-duration" className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10161" data-mp="1rnv3ra1cvrzx">
                    Duration
                  </label>
                  <div className="flex gap-3" data-id="element-10162" data-mp="2tx6sg43t161q">
                    <div className="relative flex-1" data-id="element-10163" data-mp="2p1hs3yjynl4d">
                      <select id="reward-access-duration" value={accessDuration} onChange={e => setAccessDuration(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10164" data-mp="2k1d7bwc0s48c">
                        <option data-id="element-10165" data-mp="269f8cec0vopw">30 days</option>
                        <option data-id="element-10166" data-mp="268nlbrwhqzb8">60 days</option>
                        <option data-id="element-10167" data-mp="21acbvia3yqtj">90 days</option>
                        <option data-id="element-10168" data-mp="21b583pbfxxxf">Until a set date</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10169" data-mp="1wdc492xm1g0b" />
                    </div>
                    {accessDuration === 'Until a set date' && <input type="date" value={accessEndDate} onChange={e => setAccessEndDate(e.target.value)} aria-label="Access end date" className="flex-1 h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none" data-id="element-10170" data-mp="1nbmctx3qah2j" />}
                  </div>
                  <p className="text-xs text-[#64748B] mt-1.5" data-id="element-10171" data-mp="brxbtyjsm413">
                    Members get access to these items for the duration, starting
                    when they claim the reward.
                  </p>
                </div>
              </div>
            </section>}

          {(rewardType === 'Discount Code' || rewardType === 'Free Product') && <section data-id="element-10172" data-mp="3p7ligeq2s125">
              <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10173" data-mp="3p720wz4c7eon">
                How It's Applied
              </h3>
              <div className="space-y-2" data-id="element-10174" data-mp="2wk8sdukphb43">
                {[{
              id: 'automatic' as const,
              label: 'Applied automatically at checkout',
              help: 'The reward is added to the order as soon as the member claims it.'
            }, {
              id: 'code' as const,
              label: 'Member receives a code',
              help: 'The member enters the code at checkout to redeem it.'
            }].map(option => <label key={option.id} className="flex items-start gap-2 cursor-pointer" data-id="element-10175" data-mp="336uog07ikren">
                    <input type="radio" name="applyMethod" checked={applyMethod === option.id} onChange={() => setApplyMethod(option.id)} className="mt-0.5 text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10176" data-mp="2ya4b3kn3g9wx" />
                    <span data-id="element-10177" data-mp="26iye3d2o6sbv">
                      <span className="block text-sm text-[#0F172A]" data-id="element-10178" data-mp="26jpxzqmwn1mb">
                        {option.label}
                      </span>
                      <span className="block text-xs text-[#64748B]" data-id="element-10179" data-mp="2fiuxp0a7d102">
                        {option.help}
                      </span>
                    </span>
                  </label>)}
              </div>
            </section>}

          {/* Section 3: Audience gates */}
          <section data-id="element-10180" data-mp="3qzun9o5611qe">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10181" data-mp="1cpz8o109vvsu">
              Who can claim this reward
            </h3>
            <div className="space-y-5" data-id="element-10182" data-mp="1hqn7v9ynniy5">
              {/* Gate 1: Minimum tier */}
              <div className="flex flex-col" data-id="element-10183" data-mp="13qceo09vhs9t">
                <div className="flex items-center justify-between" data-id="element-10184" data-mp="13rnjm3f2162i">
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10185" data-mp="18oxehyl7q9xq">
                    Minimum tier
                  </span>
                  <button onClick={() => handleToggle('minimumTier')} aria-pressed={toggles.minimumTier} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${toggles.minimumTier ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10186" data-mp="1wgccxg9vc73p">
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${toggles.minimumTier ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10187" data-mp="1rm856uss3v1k" />
                  </button>
                </div>
                {toggles.minimumTier && <div className="mt-3 animate-ocean-slide-up" data-id="element-10188" data-mp="vi9e91wicm4o">
                    <div className="relative" data-id="element-10189" data-mp="vfdf5a8xbren">
                      <select value={minTier} onChange={e => setMinTier(e.target.value)} aria-label="Minimum tier" className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10190" data-mp="qhlkw9kt3t7a">
                        <option data-id="element-10191" data-mp="1e6xuvbnyk5an">Enthusiast</option>
                        <option data-id="element-10192" data-mp="1a6m89tc1buwj">Connoisseur</option>
                        <option data-id="element-10193" data-mp="1a75tbiwzezes">Sommelier</option>
                        <option data-id="element-10194" data-mp="15fnscuuykytt">Vintner</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10195" data-mp="15dkrtgw6jnau" />
                    </div>
                  </div>}
              </div>

              {/* Gate 2: Tag restriction */}
              <div className="pt-5 border-t border-[#E2E8F0]" data-id="element-10196" data-mp="1t58f7ou8gg8d">
                <span className="text-sm font-medium text-[#0F172A] block mb-2" data-id="element-10197" data-mp="20gmq8lwpffyx">
                  Tag restriction
                </span>
                <div className="space-y-2" data-id="element-10198" data-mp="25gsm6ea0bomt">
                  {[{
                  id: 'all' as const,
                  label: 'All members'
                }, {
                  id: 'tags' as const,
                  label: 'Only members with these tags'
                }].map(option => <label key={option.id} className="flex items-center gap-2 cursor-pointer" data-id="element-10199" data-mp="2jg0npdtn78u6">
                      <input type="radio" name="audienceMode" checked={audienceMode === option.id} onChange={() => setAudienceMode(option.id)} className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10200" data-mp="2oebyprvobpdz" />
                      <span className="text-sm text-[#0F172A]" data-id="element-10201" data-mp="2y4594pk2xygn">
                        {option.label}
                      </span>
                    </label>)}
                </div>

                {audienceMode === 'tags' && <div className="mt-3 space-y-3 animate-ocean-slide-up" data-id="element-10202" data-mp="374jvls8s5jlq">
                    <div data-id="element-10203" data-mp="372qlg2yykh8z">
                      <span className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10204" data-mp="172c0a4qf0qze">
                        Customer tags
                      </span>
                      <div className="flex flex-wrap gap-2" data-id="element-10205" data-mp="12z5ka5di3lfq">
                        {allTags.map(tag => <Tag key={tag.id} label={tag.label} color={tag.color} selected={restrictedTags.includes(tag.id)} onClick={() => toggleRestrictedTag(tag.id)} data-id="element-10206" data-mp="1gu7w1951450g" />)}
                      </div>
                      <p className="text-xs text-[#64748B] mt-2" data-id="element-10207" data-mp="1qp9ly774pb43">
                        A member qualifies if they carry any one of the selected
                        tags.
                      </p>
                    </div>

                    {/* Reach check */}
                    {restrictedTags.length > 0 && (matchedMemberCount === 0 ? <div className="rounded-md border border-[#FDE68A] bg-[#FEF3C7] px-3 py-2.5 flex items-start gap-2" data-id="element-10208" data-mp="1uqlfuox1cii3">
                          <AlertTriangle className="w-4 h-4 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-10209" data-mp="en68lutdklo" />
                          <p className="text-xs text-[#92400E] m-0" data-id="element-10210" data-mp="2c20hzu3mb2ne">
                            No members currently carry these tags, so nobody can
                            claim this reward. Check the tags before you save.
                          </p>
                        </div> : <p className="text-xs text-[#64748B] m-0" data-id="element-10211" data-mp="237c9zw6nr6uw">
                          Approximately{' '}
                          <span className="font-semibold text-[#0F172A]" data-id="element-10212" data-mp="278nvbpii15xa">
                            {matchedMemberCount.toLocaleString()}
                          </span>{' '}
                          members currently match these tags.
                        </p>)}
                  </div>}
              </div>

              {/* Gate 3: Minimum spend */}
              <div className="pt-5 border-t border-[#E2E8F0] flex flex-col" data-id="element-10213" data-mp="1ocxonozmfld2">
                <div className="flex items-center justify-between" data-id="element-10214" data-mp="3eo280s0xgsdf">
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10215" data-mp="3emr3ur3klils">
                    Minimum spend
                  </span>
                  <button onClick={() => handleToggle('minimumSpend')} aria-pressed={toggles.minimumSpend} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${toggles.minimumSpend ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10216" data-mp="26h3jhz4vu5o9">
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${toggles.minimumSpend ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10217" data-mp="1woxripemro92" />
                  </button>
                </div>
                {toggles.minimumSpend && <div className="mt-3 space-y-3 animate-ocean-slide-up" data-id="element-10218" data-mp="1ipog031ddibf">
                    <div className="relative" data-id="element-10219" data-mp="1dt7n6vj86fs0">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748B] pointer-events-none" data-id="element-10220" data-mp="1dtzkcash0lke">
                        $
                      </span>
                      <input type="text" inputMode="decimal" value={minSpendAmount} onChange={e => setMinSpendAmount(e.target.value)} placeholder="5,000" aria-label="Minimum spend amount" className="w-full h-10 pl-7 pr-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none bg-white" data-id="element-10221" data-mp="385ebmw14qt29" />
                    </div>

                    <div data-id="element-10222" data-mp="3kirkkngknxid">
                      <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10223" data-mp="3pg1eogewoxcj">
                        Spend in
                      </label>
                      <div className="relative" data-id="element-10224" data-mp="2sr29xbdj6dp3">
                        <select value={minSpendPeriod} onChange={e => setMinSpendPeriod(e.target.value)} className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10225" data-mp="2wqb2icwu45j2">
                          <option data-id="element-10226" data-mp="h0hqvwx0874v">Last 12 months</option>
                          <option data-id="element-10227" data-mp="h1sxe4hu3szo">This calendar year</option>
                          <option data-id="element-10228" data-mp="lxhxc8xrnbwi">This financial year (1 July to 30 June)</option>
                          <option data-id="element-10229" data-mp="1xueeewjrfhx6">All time</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10230" data-mp="22sfxhhh6rh8n" />
                      </div>
                    </div>

                    <div data-id="element-10231" data-mp="2ciiwbyaznpew">
                      <span className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10232" data-mp="2cisg345fx0c5">
                        Orders that count
                      </span>
                      <div className="space-y-2" data-id="element-10233" data-mp="1ex3gbf9v6ujb">
                        {[{
                      id: 'all' as const,
                      label: 'All orders'
                    }, {
                      id: 'club' as const,
                      label: 'Club orders only'
                    }, {
                      id: 'oneTime' as const,
                      label: 'One-time orders only'
                    }].map(option => <label key={option.id} className="flex items-center gap-2 cursor-pointer" data-id="element-10234" data-mp="349f1ecqt4prm">
                            <input type="radio" name="minSpendScope" checked={minSpendScope === option.id} onChange={() => setMinSpendScope(option.id)} className="text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-10235" data-mp="sm3fm0s0y41t" />
                            <span className="text-sm text-[#0F172A]" data-id="element-10236" data-mp="jthz6rbv89g5">
                              {option.label}
                            </span>
                          </label>)}
                      </div>
                    </div>

                    <p className="text-xs text-[#64748B] m-0" data-id="element-10237" data-mp="9wn1ganf2d2x">
                      Members who have spent at least this much in the chosen
                      period can claim this reward.
                    </p>
                  </div>}
              </div>

              {/* Gate 4: Minimum tenure */}
              <div className="pt-5 border-t border-[#E2E8F0] flex flex-col" data-id="element-10238" data-mp="3s31dg424kcyo">
                <div className="flex items-center justify-between" data-id="element-10239" data-mp="1v85abc0a4hy6">
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10240" data-mp="1qad73lu6p9eo">
                    Minimum tenure
                  </span>
                  <button onClick={() => handleToggle('minimumTenure')} aria-pressed={toggles.minimumTenure} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${toggles.minimumTenure ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10241" data-mp="29dnpem7oeb3y">
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${toggles.minimumTenure ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10242" data-mp="25d4weou1qz06" />
                  </button>
                </div>
                {toggles.minimumTenure && <div className="mt-3 space-y-2 animate-ocean-slide-up" data-id="element-10243" data-mp="2x26wiyezohug">
                    <div className="flex items-center gap-3" data-id="element-10244" data-mp="2j408e9zudw7z">
                      <input type="number" min="1" value={minTenureValue} onChange={e => setMinTenureValue(e.target.value)} aria-label="Minimum tenure" className="w-24 h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none bg-white" data-id="element-10245" data-mp="2o33cns7zzyew" />
                      <div className="relative flex-1" data-id="element-10246" data-mp="153lydqwl8cb0">
                        <select value={minTenureUnit} onChange={e => setMinTenureUnit(e.target.value as 'Months' | 'Years')} aria-label="Tenure unit" className="w-full h-10 pl-3 pr-8 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none appearance-none bg-white" data-id="element-10247" data-mp="1063zyug75fh9">
                          <option data-id="element-10248" data-mp="lhr21rxjtmk4">Months</option>
                          <option data-id="element-10249" data-mp="hfckrc9j2uuy">Years</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" data-id="element-10250" data-mp="hd9l00ikpnd1" />
                      </div>
                    </div>
                    <p className="text-xs text-[#64748B] m-0" data-id="element-10251" data-mp="qepum61kdlg7">
                      Counted from each member's club start date. You can change
                      this in Program Settings.
                    </p>
                  </div>}
              </div>

              {/* Gate 5: Club order streak */}
              <div className="pt-5 border-t border-[#E2E8F0] flex flex-col" data-id="element-10252" data-mp="bbzhk4fik9uh">
                <div className="flex items-center justify-between" data-id="element-10253" data-mp="gdoszl6nepj3">
                  <span className="text-sm font-medium text-[#0F172A]" data-id="element-10254" data-mp="lc02tvwy101b">
                    Club order streak
                  </span>
                  <button onClick={() => handleToggle('clubStreak')} aria-pressed={toggles.clubStreak} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${toggles.clubStreak ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10255" data-mp="q4jjx2uuadav">
                    <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${toggles.clubStreak ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10256" data-mp="14313codpp3mz" />
                  </button>
                </div>
                {toggles.clubStreak && <div className="mt-3 space-y-2 animate-ocean-slide-up" data-id="element-10257" data-mp="35wh4if7fe0w7">
                    <div data-id="element-10258" data-mp="3athayr86t5nu">
                      <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-10259" data-mp="1ry3050x34km1">
                        Club orders in a row
                      </label>
                      <input type="number" min="1" value={clubStreakCount} onChange={e => setClubStreakCount(e.target.value)} placeholder="10" className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none bg-white" data-id="element-10260" data-mp="1n5tgu3qh6isq" />
                    </div>
                    <p className="text-xs text-[#64748B] m-0" data-id="element-10261" data-mp="26v3ulc6nuto7">
                      A skip, pause or hold resets the streak. A delayed shipment
                      or failed payment doesn't.
                    </p>
                  </div>}
              </div>

              {/* How the gates combine */}
              <p className="text-xs text-[#64748B] flex items-start gap-1.5 m-0" data-id="element-10262" data-mp="qi582ccb02lu">
                <Info className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0 mt-0.5" data-id="element-10263" data-mp="lhhi7tzt9rt7" />
                Every rule you turn on applies together, so a member has to meet
                all of them to claim this reward.
              </p>
            </div>
          </section>

          {/* Section 4: Availability Rules */}
          <section data-id="element-10264" data-mp="dxge0s5j7zex">
            <h3 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-10265" data-mp="dxzus5jg4hrd">
              Availability Rules
            </h3>
            <div className="space-y-4" data-id="element-10266" data-mp="48yiapc40ii5">
              {[{
              key: 'inventoryLimit',
              label: 'Inventory Limit'
            }, {
              key: 'perCustomerLimit',
              label: 'Per-Customer Limit'
            }, {
              key: 'cooldown',
              label: 'Cooldown'
            }, {
              key: 'dateRange',
              label: 'Date Range'
            }].map(rule => <div key={rule.key} className="flex flex-col" data-id="element-10267" data-mp="3heot548vkl5y">
                  <div className="flex items-center justify-between" data-id="element-10268" data-mp="3hhb9ntdqub0b">
                    <span className="text-sm font-medium text-[#0F172A]" data-id="element-10269" data-mp="3md05pmqk19rv">
                      {rule.label}
                    </span>
                    <button onClick={() => handleToggle(rule.key as keyof typeof toggles)} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${toggles[rule.key as keyof typeof toggles] ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-10270" data-mp="32uck63yg5qo8">
                      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${toggles[rule.key as keyof typeof toggles] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-10271" data-mp="37qtbt84uun62" />
                    </button>
                  </div>
                </div>)}
            </div>
          </section>
        </div>

        {/* Drawer Footer */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-white flex items-center justify-end gap-3 flex-shrink-0" data-id="element-10272" data-mp="3qj593ggniy9c">
          <Button variant="ghost" size="md" onClick={closeDrawer} data-id="element-10273" data-mp="8d0n3amzn5ja">
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={closeDrawer} data-id="element-10274" data-mp="4cqju5qkyt84">
            Save Reward
          </Button>
        </div>
      </div>
    </div>;
}